<script setup lang="ts">
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { onBeforeUnmount, onMounted, ref } from 'vue'
import LoginUser from './LoginUser.vue'
import LogoutUser from './LogoutUser.vue'
import RegisterUser from './RegisterUser.vue'

const store = useStore()
const user = computed(() => store.user)
const isVip = computed(() => store.pageViews.isVip)

interface Props {
  isVisible: boolean
}

interface Emits {
  (event: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const panel = ref<HTMLElement | null>(null)

function close() {
  emit('close')
}

function handleEscape(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    close()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape)
})
</script>

<template>
  <template>
    <TransitionRoot as="template" :show="isVisible">
      <Dialog class="relative z-10" @close="close">
        <TransitionChild
          as="template"
          enter="ease-in-out duration-500"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in-out duration-500"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" @click="close" />
        </TransitionChild>

        <div class="fixed inset-0 overflow-hidden">
          <div class="absolute inset-0 overflow-hidden">
            <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <TransitionChild
                as="template"
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enter-from="translate-x-full"
                enter-to="translate-x-0"
                leave="transform transition ease-in-out duration-300 sm:duration-500"
                leave-from="translate-x-0"
                leave-to="translate-x-full"
              >
                <DialogPanel class="pointer-events-auto w-screen max-w-md">
                  <div class="flex h-full flex-col overflow-y-scroll bg-white py-6 shadow-xl">
                    <div class="relative flex-1 px-4 sm:px-6">
                      <div v-if="user">
                        <LogoutUser />
                      </div>
                      <div v-else>
                        <LoginUser />
                        <div v-if="!isVip" class="mt-4">
                          <h2>Rekisteröidy</h2>

                          <p class="text-sm">
                            Tämän sivuston keskustelut on varattu vain aktiivisimmille lukijoille. Kun olet selannut
                            tätä sivustoa samalla selaimella (laitteella) tarpeeksi pitkään, näet tässä
                            rekisteröitymislomakkeen. Kiitos kärsivällisyydestäsi!
                          </p>
                        </div>
                        <div v-else class="mt-4">
                          <RegisterUser />
                        </div>
                      </div>
                      <hr class="mt-6" />

                      <div class="pt-6 text-center">
                        <p class="text-sm underline cursor-pointer" @click="close">sulje</p>
                      </div>

                      <div class="pt-8 text-center">
                        <p class="text-xs opacity-50 font-sans something">
                          Jos tarvitset apua käyttäjätunnukseesi liittyvässä asiassa, lähetä sähköpostia osoitteeseen
                          ville@unessa.net
                        </p>
                      </div>
                    </div>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </template>
</template>
